/**
 * End-user settings owned by the **platform shell** (见 05 / §5.12).
 *
 * The counterpart of `PLATFORM_PRODUCT_CONFIG`, one layer up: those keys are
 * deployment facts resolved at boot and read-only at runtime, these are settings
 * the user edits in the settings panel and that persist in the `user` scope
 * layer. Both exist for the same reason — code living in the platform packages
 * has no manifest to contribute through.
 *
 * They are aggregated **before** module declarations by `listSettings()`, so a
 * module cannot shadow a shell key by declaring the same one, and they render
 * through the same generic settings renderer a module's do — including reset and
 * the shadowed-layer badge. Their `%key%` titles resolve under the `platform:`
 * message namespace, exactly like the platform's built-in commands do.
 */
import type { SettingContribution } from './index';
import { NETWORK_USE_SYSTEM_PROXY_KEY } from './network-proxy';

/**
 * Whether the platform may pull the user's attention back to the app — flashing
 * the taskbar button, bouncing the dock, alternating a browser tab's title
 * (见 23).
 *
 * Turning it off silences the *pulse* only. Badges keep rendering: they are a
 * passive record of unread work, not an interruption, and hiding them would lose
 * information rather than reduce noise.
 */
export const ATTENTION_FLASH_KEY = 'workbench.attention.flash';

/**
 * Do Not Disturb — no notification reaches the user while it is on (见 23).
 *
 * **This is also the global notification switch**, deliberately one key rather
 * than two: 需求 §4.4.4 lists 勿扰 and §4.4.5 lists 桌面通知开关, but both mean
 * exactly the same thing (no notification, unread and badges keep counting), and
 * two keys would produce combinations nobody can answer — "主开关关着而勿扰没开,
 * 到底弹不弹".
 *
 * It outranks a module's `urgency: 'critical'`: urgency is the module's judgement
 * about its own event, this is the user's judgement about being interrupted at
 * all (same ordering as {@link ATTENTION_FLASH_KEY}).
 */
export const NOTIFICATION_DND_KEY = 'workbench.notification.dnd';

/**
 * Epoch milliseconds a *timed* Do Not Disturb lapses at; `0` = none (见 23).
 *
 * Backs the tray's「1 小时 / 今天剩余时间」(需求 §4.6.2). Stored as the deadline
 * rather than as a switch plus a timer because the deadline survives a restart
 * on its own — a session that ends before it lapses comes back with DND already
 * off, and nothing has to write the switch back.
 *
 * Separate from {@link NOTIFICATION_DND_KEY} despite meaning the same thing while
 * active: turning the standing switch off must not erase a timed window the user
 * set, and a lapsed deadline must not read as "the user wants notifications" if
 * they also flipped the standing switch.
 */
export const NOTIFICATION_DND_UNTIL_KEY = 'workbench.notification.dndUntil';

/**
 * Turn global notifications back on (见 23 启动提醒).
 *
 * Writes {@link NOTIFICATION_DND_KEY} and {@link NOTIFICATION_DND_UNTIL_KEY}
 * both off — the same pair the tray's「关闭」writes. Clearing only the standing
 * switch leaves a timed deadline in place, and notifications stay dropped.
 *
 * Handled in the extension host rather than registered by a module, so every
 * window's `executeCommand` reaches it.
 */
export const NOTIFICATION_RESUME_COMMAND = 'workbench.notification.resume';

/**
 * Whether a delivered notification plays the bundled chime (见 23 声音).
 *
 * Both surfaces, on desktop and on the web: an OS notification and an in-app
 * toast play the same clip. The OS notification itself stays silent, so its
 * own sound does not play on top of the clip.
 */
export const NOTIFICATION_SOUND_KEY = 'workbench.notification.sound';

/**
 * Whether closing the workbench window hides it to the tray instead of ending
 * the session (见 23 托盘).
 *
 * A boolean rather than a `close: 'tray' | 'quit'` enum because the settings
 * renderer has no way to label enum options — it lists the raw values — so an
 * enum would put untranslated tokens in front of the user. Two behaviours, one
 * of which is the default, is a checkbox anyway.
 *
 * Desktop-only: there is no tray to hide into on the web, and the browser owns
 * what closing a tab means.
 */
export const CLOSE_TO_TRAY_KEY = 'workbench.window.closeToTray';

/**
 * Whether the minimize button puts the window in the tray instead of the taskbar
 * (需求 §4.6.3，见 23 托盘).
 *
 * The other half of {@link CLOSE_TO_TRAY_KEY}, and **defaulted the other way**:
 * minimizing is the gesture for "keep it one click away", and the taskbar button
 * is that one click. Sending it to the tray by default would make the two buttons
 * on the title bar do the same thing.
 *
 * Desktop-only, same reason as the key above.
 */
export const MINIMIZE_TO_TRAY_KEY = 'workbench.window.minimizeToTray';

/**
 * Whether a bare Escape that nothing in the page consumed closes the window
 * (见 08 Esc 关窗).
 *
 * "Close" means the same thing the title bar's close button means, so the main
 * window still obeys {@link CLOSE_TO_TRAY_KEY} while a detached or shell window
 * really closes. The two keys therefore compose rather than overlap: this one
 * decides whether Escape is a close at all, that one decides what a close does.
 *
 * A switch exists because Escape is the most contested key in the app — anyone
 * who habitually presses it to leave a field would otherwise lose the window.
 * Desktop-only: on the web the page has no window of its own to close.
 */
export const ESCAPE_CLOSES_WINDOW_KEY = 'workbench.window.escapeCloses';

/**
 * Whether the platform checks for a newer build on its own (见 25).
 *
 * Turning it off silences only the *polling*; the settings panel's「检查更新」
 * button keeps working, because asking explicitly is not the thing anyone wants
 * to switch off.
 */
export const UPDATE_AUTO_CHECK_KEY = 'update.autoCheck';

/**
 * Whether a found update starts downloading without being asked (见 25).
 *
 * Separate from {@link UPDATE_AUTO_CHECK_KEY} because "tell me" and "spend my
 * bandwidth" are different consents — a tethered connection is the case this
 * exists for. Off means the prompt appears with a Download button instead of a
 * progress bar.
 */
export const UPDATE_AUTO_DOWNLOAD_KEY = 'update.autoDownload';

export const PLATFORM_SETTINGS: readonly SettingContribution[] = [
  // The three notification keys carry `ownedUi` because the settings panel gives
  // them a section of their own (grouped with the「在系统设置中开启通知」link, so
  // the two halves of "why am I not getting notifications" sit together). Listing
  // them under the generic platform rows as well would show each switch twice.
  {
    key: NOTIFICATION_DND_KEY,
    type: 'boolean',
    default: false,
    title: '%setting.notification.dnd%',
    ownedUi: true,
  },
  {
    key: NOTIFICATION_DND_UNTIL_KEY,
    type: 'number',
    default: 0,
    title: '%setting.notification.dndUntil%',
    // Not a control at all: it is a deadline the tray menu writes and the
    // notification section reads back as「勿扰中，x 后恢复」. A generic number row
    // would put a raw epoch timestamp in front of the user.
    ownedUi: true,
  },
  {
    key: NOTIFICATION_SOUND_KEY,
    type: 'boolean',
    default: true,
    title: '%setting.notification.sound%',
    ownedUi: true,
  },
  {
    key: ATTENTION_FLASH_KEY,
    type: 'boolean',
    default: true,
    title: '%setting.attention.flash%',
    // Belongs with the notification switches: "把我叫回来" and "告诉我" are the two
    // halves of one question for anyone opening settings to turn the noise down.
    ownedUi: true,
  },
  {
    key: CLOSE_TO_TRAY_KEY,
    type: 'boolean',
    default: true,
    title: '%setting.window.closeToTray%',
    runtimes: ['desktop'],
  },
  {
    key: MINIMIZE_TO_TRAY_KEY,
    type: 'boolean',
    default: false,
    title: '%setting.window.minimizeToTray%',
    runtimes: ['desktop'],
  },
  {
    key: ESCAPE_CLOSES_WINDOW_KEY,
    type: 'boolean',
    default: true,
    title: '%setting.window.escapeCloses%',
    runtimes: ['desktop'],
  },
  {
    key: UPDATE_AUTO_CHECK_KEY,
    type: 'boolean',
    default: true,
    title: '%setting.update.autoCheck%',
  },
  {
    key: UPDATE_AUTO_DOWNLOAD_KEY,
    type: 'boolean',
    default: true,
    title: '%setting.update.autoDownload%',
    // Desktop-only: the web "download" is the page reload itself, so a switch
    // here would claim to control something that does not exist over there.
    runtimes: ['desktop'],
  },
  {
    key: NETWORK_USE_SYSTEM_PROXY_KEY,
    type: 'boolean',
    default: true,
    title: '%setting.network.useSystemProxy%',
    // Desktop-only: on the web the browser owns egress, and there is no Node
    // process whose stack could disagree with it (见 24 §5.3).
    runtimes: ['desktop'],
  },
];
